import React, { createContext, useState, useMemo, useEffect, useContext } from 'react';

import { useConfigContext } from './ConfigContext';

import { AccessData } from '../types';
import { useLanguageContextAPI } from './LanguageContext';
import { useLoaderData, useNavigate } from 'react-router';
import { getApiBaseUrl } from '../utils/apiBase';
import DisclaimerComponent from '../components/Disclaimer';
import { Typography } from '@airbus/components-react';

export const UserContext = createContext(null);

export function hasMonthsPassed(
  inputDate: string,
  months: number,
  currentDate: string = null,
): boolean {
  // Ensure the inputDate is a valid Date object
  const inputDateObj = new Date(inputDate);
  let currentDateObj: Date;
  if (!currentDate) currentDateObj = new Date();
  else currentDateObj = new Date(currentDate);
  if (isNaN(inputDateObj.getTime()) || isNaN(currentDateObj.getTime())) {
    return false;
  }

  // Add 6 months to the given date
  const monthsLaterObj = new Date(inputDateObj);
  monthsLaterObj.setMonth(monthsLaterObj.getMonth() + months);
  
  // Normalize both dates to day-level for comparison (ignore time)
  const normalizedCurrent = new Date(
    currentDateObj.getUTCFullYear(),
    currentDateObj.getUTCMonth(),
    currentDateObj.getUTCDate()
  );
  const normalizedMonthsLater = new Date(
    monthsLaterObj.getUTCFullYear(),
    monthsLaterObj.getUTCMonth(),
    monthsLaterObj.getUTCDate()
  );
  return normalizedCurrent >= normalizedMonthsLater;
}

async function updateLastLogin(userId: string) {
  const now = new Date().toISOString().substring(0, 19);
  const set = { last_login: now };
  const apiBase = getApiBaseUrl();
  return await fetch(`${apiBase}/api/userSession/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ set }),
  });
}

async function fetchUserData({
  loginDetails,
  apiBase,
  configSchema,
  setUserDetails,
  setAccessControlDetails,
  setDisclaimerOpen,
  setAcceptDisclaimer,
  navigate,
}: any) {
  if (!loginDetails?.userId) return;

  updateLastLogin(loginDetails.userId);

  const userRes = await fetch(
    `${apiBase}/api/userRoleDetails?` +
      new URLSearchParams({
        userAccountId: loginDetails.userId,
        configSchema,
      }),
  );

  const userDetails = await userRes.json();
  const userData = userDetails?.[`${configSchema}_mbd_user_to_role`];

  if (!userData?.length) {
    setAccessControlDetails([]);
    return;
  }

  const roleIds = userData.map(({ roleId }: any) => roleId);
  setUserDetails({ ...userData[0], roleIds });

  const lastAcceptDate = userData[0]?.mbdUserByUserId?.disclaimerAcceptDate;

  if (!lastAcceptDate || hasMonthsPassed(lastAcceptDate, 6)) {
    setDisclaimerOpen(true);
    setAcceptDisclaimer(false);
  }

  const accessRes = await fetch(
    `${apiBase}/api/screenAccess?` + new URLSearchParams({ userAccountId: loginDetails.userId }),
  );

  const accessControlData = await accessRes.json();
  const accessData = accessControlData?.[`${configSchema}_mbd_roles_to_screens`];

  if (!accessData?.length) {
    navigate('/404');
    return;
  }

  const access = new Map(accessData.map((item: any) => [item.mbdScreen.screenInternalName, item]));

  setAccessControlDetails([...access.values()]);
}

export const UserContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { loginDetails } = useLoaderData();

  const [userDetails, setUserDetails] = useState(null); //user_id: '', role_id: "", screen_id: ""
  const [accessControlDetails, setAccessControlDetails] = useState<AccessData>(null);
  const [subMenuDetails, setSubMenuDetails] = useState({});

  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(true);

  const { configSchema } = useConfigContext();
  const { t } = useLanguageContextAPI();
  const apiBase = getApiBaseUrl();

  useEffect(() => {
    fetchUserData({
      loginDetails,
      apiBase,
      configSchema,
      setUserDetails,
      setAccessControlDetails,
      setDisclaimerOpen,
      setAcceptDisclaimer,
      navigate,
    });
  }, [loginDetails, apiBase, configSchema, navigate]);

  const updateUserDetails = (obj) => {
    setUserDetails((prestate) => ({ ...prestate, ...obj }));
  };

  const updateSubMenuDetails = (subMenu) => {
    setSubMenuDetails((prestate) => ({ ...prestate, ...subMenu }));
  };

  const updateAccessControlDetails = (accessControlData) => {
    setAccessControlDetails(accessControlData);
  };

  const updateDisclaimerOpen = (state) => {
    setDisclaimerOpen(state);
  };

  const updateAcceptDisclaimer = (state) => {
    setAcceptDisclaimer(state);
  };

  const value = useMemo(
    () => ({
      userDetails,
      updateUserDetails,
      accessControlDetails,
      updateAccessControlDetails,
      subMenuDetails,
      updateSubMenuDetails,
      loginDetails,
      disclaimerOpen,
      updateDisclaimerOpen,
      acceptDisclaimer,
      updateAcceptDisclaimer,
    }),
    [
      updateUserDetails,
      userDetails,
      subMenuDetails,
      accessControlDetails,
      updateAccessControlDetails,
      updateSubMenuDetails,
      loginDetails,
      disclaimerOpen,
      updateDisclaimerOpen,
      acceptDisclaimer,
      updateAcceptDisclaimer,
    ],
  );

  if (!acceptDisclaimer) {
    return (
      <>
        <Typography variant='h6'>{t('landing_page.no_accept_disclaimer')}</Typography>
        <DisclaimerComponent
          {...{
            disclaimerOpen,
            updateDisclaimerOpen,
            updateAcceptDisclaimer,
          }}
        />
      </>
    );
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
export default UserContextProvider;

export function useUserContextApi() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('Context must be used within a Provider');
  }
  return context;
}
